/**
 * firmasDelActa.js — quién ha firmado ya, para marcarlo en la tabla.
 *
 * ── Para qué ─────────────────────────────────────────────────────────────
 *
 * La rúbrica dibujada se estampa en la columna «Firma» de la fila de esa
 * persona, dentro del documento. Pero en el panel de la minuta esa columna
 * aparecía igual de vacía antes y después de firmar, así que no había manera de
 * saber por dónde iba la ronda sin abrir el documento o el diálogo de firmas.
 *
 * Esto responde la pregunta que faltaba: de los que están en la tabla, ¿quién
 * firmó ya? Con eso se pone una palomita en su fila.
 *
 * ── Dos reglas que no se pueden inventar aquí ────────────────────────────
 *
 * **Cuál firma vale.** Un acta puede tener varias rondas: se mandó, alguien la
 * rechazó, se corrigió y se volvió a mandar. Una persona puede haber firmado en
 * dos. Vale la MÁS RECIENTE, porque es la que firmó el documento que hay ahora.
 * Es la misma regla que usa el servidor al estampar —`firmasDibujadas`, en
 * SignatureRoundRepository, con `ORDER BY r.Numero DESC`—, y tiene que serlo:
 * si aquí se contara otra, la palomita diría una cosa y el documento otra.
 *
 * **Quién es quién.** El emparejado de nombres no se repite aquí: se usa
 * `mismoNombre` de [cotejarFirmantes], que ya es la copia de la regla del
 * servidor y se mantiene en paso con ella desde `probar-firma-en-acta.js`.
 * Tener dos reglas de nombres en el navegador sería tener dos formas de
 * equivocarse.
 */
// Con la extension: webpack la resuelve sin ella, pero Node no, y este modulo
// se carga tal cual desde `probar-palomita-de-firma.mjs`.
import { mismoNombre } from './cotejarFirmantes.js';

/**
 * De las rondas de un acta, quién ha aprobado.
 *
 * @param {Array} rondas - lo que devuelve `listarFirmasDeMinuta`, ya en orden
 *        de más reciente a más antigua, que es como lo manda el servidor.
 * @returns {Array} [{ nombre, correo, cuando }] sin repetidos.
 *
 * Solo `aprobada`. Quien rechazó no firmó, y quien está pendiente tampoco:
 * marcar cualquier otra cosa convertiría la palomita en «ya le llegó», que es
 * una pregunta distinta y menos útil.
 */
export function yaFirmaron(rondas = []) {
  const vistas = new Set();
  const firmados = [];

  for (const ronda of rondas || []) {
    for (const f of (ronda && ronda.firmantes) || []) {
      if (!f || f.estado !== 'aprobada') continue;
      // Por correo cuando lo hay: dos personas pueden llamarse igual, y dos
      // grafías del mismo nombre son la misma persona.
      const clave = String(f.correo || f.nombre || '').toLowerCase();
      if (!clave || vistas.has(clave)) continue;
      vistas.add(clave);
      firmados.push({ nombre: f.nombre, correo: f.correo, cuando: f.decididoEl || null });
    }
  }

  return firmados;
}

/**
 * La firma que corresponde a una fila de la tabla de asistentes, si la hay.
 *
 * Devuelve el firmante o `null`. Se compara por nombre porque es lo único que
 * tiene la fila del acta: ahí no hay correos.
 */
export function firmaDeLaFila(nombreEnLaFila, firmados = []) {
  const nombre = String(nombreEnLaFila || '').trim();
  if (!nombre) return null;
  return (firmados || []).find(f => f && mismoNombre(nombre, f.nombre)) || null;
}

/** «14 oct 2026», para el título de la palomita. Sin hora: no aporta y estorba. */
export function cuandoFirmo(cuando) {
  if (!cuando) return null;
  const d = new Date(cuando);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
}
